import { AtWorkLocation, IIdentityClaims, UserStatus } from './types';
import { atom } from 'recoil';
import type { IStoredStatus } from './statusPersistence';

export const aIdentityClaims = atom<IIdentityClaims | undefined>({ key: 'aIdentityClaims', default: undefined });

export const aProfileImage = atom<string | undefined>({ key: 'aProfileImage', default: undefined });

// User status atom - defaults to 'available' (at work)
export const aUserStatus = atom<UserStatus | undefined>({ key: 'aUserStatus', default: 'available' });

// At-work location (Office, Remote, Other, Custom) - default Office; icon changes by selection
export const aAtWorkLocation = atom<AtWorkLocation>({ key: 'aAtWorkLocation', default: 'office' });

// Custom phrase when atWorkLocation is 'custom' - shown to others instead of default "At work"
export const aAtWorkCustomPhrase = atom<string | undefined>({ key: 'aAtWorkCustomPhrase', default: undefined });

// Away status message atom - stores the selected/custom away phrase
export const aAwayStatusMessage = atom<string | undefined>({ key: 'aAwayStatusMessage', default: undefined });

// Focus (Do Not Disturb) end time - timestamp in ms; when set, show timer and "Focus work until endTime"
export const aFocusEndTime = atom<number | undefined>({ key: 'aFocusEndTime', default: undefined });

// Intended focus duration in minutes; used to clamp initial countdown display so timer starts at full value (e.g. 30:00)
export const aFocusDurationMinutes = atom<number | undefined>({ key: 'aFocusDurationMinutes', default: undefined });

// Emergency contact preferences when busy: knock on door, text message (for presence sync)
export const aFocusTimeKnockDoor = atom<boolean>({ key: 'aFocusTimeKnockDoor', default: true });
export const aFocusTimeTextMessage = atom<boolean>({ key: 'aFocusTimeTextMessage', default: true });

// When true, show "Focus time ended – extend or end?" modal (set when timer expires without user action)
export const aShowFocusExtendEndModal = atom<boolean>({ key: 'aShowFocusExtendEndModal', default: false });

// When true, show "Are you still on the phone?" modal with extend + 60s auto-close (set when on-call timer expires)
export const aShowOnCallEndedModal = atom<boolean>({ key: 'aShowOnCallEndedModal', default: false });

// Away extension modal: shown after 20 min on away (when isUserSelectedAwayManually). 60-min countdown, No/Yes.
export const aShowAwayExtensionModal = atom<boolean>({ key: 'aShowAwayExtensionModal', default: false });

// Timestamp when user entered away status (manually). Used for 20-min trigger.
export const aAwaySinceTimestamp = atom<number | undefined>({ key: 'aAwaySinceTimestamp', default: undefined });

// Out of Office
export interface IOutOfOfficePreferredMethods {
    email: boolean;
    chat: boolean;
    sms: boolean;
    call: boolean;
}
export const aOutOfOfficeReturnDate = atom<Date | undefined>({ key: 'aOutOfOfficeReturnDate', default: undefined });
export const aOutOfOfficeReturnTime = atom<string | null | undefined>({ key: 'aOutOfOfficeReturnTime', default: undefined }); // HH:mm when date is today, null when return date is set explicitly
export const aOutOfOfficeAbsenceComment = atom<string>({ key: 'aOutOfOfficeAbsenceComment', default: '' });
export const aOutOfOfficePreferredMethods = atom<IOutOfOfficePreferredMethods>({
    key: 'aOutOfOfficePreferredMethods',
    default: { email: false, chat: false, sms: false, call: false }
});

// Other users' status map - keyed by userId
export const aOtherUsersStatusMap = atom<Record<string, IStoredStatus>>({
    key: 'aOtherUsersStatusMap',
    default: {}
});

export const aOtherUsersStatusFromServer = atom<IStoredStatus[]>({ key: 'aOtherUsersStatusFromServer', default: [] });
